import React, {useMemo, useState} from 'react';
import FormGroup from 'components/FormGroup';
import {Controller, useForm} from 'react-hook-form';
import styles from './PropertyList.module.scss';
import Button from '../../../components/Button';
import useCompanyDetails, {CompanyPropertyForm} from '../useCompanyDetails';
import {
  GooglePlaceAutoComplete,
  TypeGooglePlaceDetail,
} from 'components/GooglePlaceAutoComplete';
import TextField from 'components/TextField/TextField';
import {get} from 'lodash';
import MapPreview from 'components/MapPreview';
import toast from 'react-hot-toast';
import DeleteModal from 'components/DeleteModal';
import {CompanyProperty} from 'types/property';
import SelectField from 'components/SelectField';
import useCompanyConfig from 'hooks/useCompanyConfig';

type AddressFormProps = {
  defaultValues?: CompanyProperty;
};

const AddressForm: React.FC<AddressFormProps> = ({
  defaultValues = {} as CompanyProperty,
}) => {
  const {control, setValue, getValues} = useForm<CompanyProperty>({
    defaultValues,
  });
  const {landlordList, deleteProperty, updateProperty, refetch} =
    useCompanyDetails();
  const [openModal, setOpenModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const {labels} = useCompanyConfig();
  const [location, setLocation] = useState({
    latitude: get(defaultValues, 'address.latitude', 0),
    longitude: get(defaultValues, 'address.longitude', 0),
  });

  const onSelectPlace = (placeDetail: TypeGooglePlaceDetail) => {
    setValue('address.zipcode', placeDetail.zipcode);
    setValue('address.address_line_1', placeDetail.addressLine1);
    setValue('address.city', placeDetail.city);
    setValue('address.state', placeDetail.state);
    setValue('address.latitude', placeDetail.lat);
    setValue('address.longitude', placeDetail.lon);
    setLocation({latitude: placeDetail.lat, longitude: placeDetail.lon});
  };

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      const data = getValues();
      await updateProperty(
        defaultValues.id,
        data as unknown as CompanyPropertyForm
      );
      refetch();
      const message = `${labels.property} updated successfully`;
      toast.success(message);
    } catch (err) {
      toast.error('Error try again');
    } finally {
      setIsUpdating(false);
    }
  };
  const landlordOptions = useMemo(
    () =>
      landlordList?.map((item) => ({
        label: `${item?.first_name} ${item?.last_name}`,
        value: String(item?.id),
      })),
    [landlordList]
  );
  return (
    <>
      <FormGroup>
        <Controller
          name="landlord"
          control={control}
          render={({field}) => (
            <SelectField
              label={`${labels.property} ${labels.landlord}`}
              value={landlordOptions?.find(
                (item) => item?.value === String(field.value)
              )}
              defaultValue={landlordOptions?.find(
                (item) => item?.value === String(field.value)
              )}
              isClearable
              options={landlordOptions}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="name"
          control={control}
          render={({field, fieldState}) => (
            <TextField
              wrapperClass={styles.wrapper}
              defaultValue={get(defaultValues, 'name', '')}
              label={`${labels.property} Name`}
              placeholder={`Enter ${labels.property} name`}
              hasError={false}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="address.city"
          control={control}
          rules={{required: 'Please, Select the address'}}
          render={({field, fieldState}) => (
            <GooglePlaceAutoComplete
              defaultValue={`${getValues('address').address_line_1}, ${
                getValues('address').city
              }, ${getValues('address').state}, ${
                getValues('address').zipcode
              }`}
              wrapperClass={styles.googleInput}
              label={`${labels.property} Address`}
              placeholder="Address"
              onPlaceSelected={onSelectPlace}
              error={fieldState.error?.message}
              options={{
                types: ['address'],
              }}
            />
          )}
        />
        <Controller
          name="address.unit_address"
          control={control}
          render={({field}) => (
            <TextField
              onChange={field.onChange}
              label="Unit Number"
              wrapperClass={styles.googleInput}
              placeholder="Enter unit number"
              defaultValue={get(defaultValues, 'address.unit_address', '')}
            />
          )}
        />
      </FormGroup>

      {!!location.latitude && (
        <MapPreview
          zoom={15}
          style={{
            marginTop: 20,
            width: 650,
            height: 350,
          }}
          onDrag={(location) => {
            setLocation({latitude: location.lat, longitude: location.lon});
            setValue('address.latitude', location.lat);
            setValue('address.longitude', location.lon);
          }}
          center={[location.latitude, location.longitude]}
        />
      )}

      <div className={styles.actionWrapper}>
        <Button
          style={{
            backgroundColor: '#fff',
          }}
          color="danger"
          onClick={() => setOpenModal(true)}
        >
          Delete
        </Button>
        <Button
          style={{
            marginLeft: 10,
            backgroundColor: '#fff',
          }}
          type="submit"
          onClick={handleUpdate}
          loading={isUpdating}
        >
          Save {labels.property}
        </Button>
      </div>
      <DeleteModal
        onDelete={async () => {
          await deleteProperty(get(defaultValues, 'id', 0));
          toast.success(`${labels.property} deleted successfully`);
          setOpenModal(false);
        }}
        text={`Are you sure you want to delete this ${labels.property}?`}
        setOpenModal={setOpenModal}
        openModal={openModal}
      />
    </>
  );
};

export default AddressForm;
